import Seo from "@/components/Seo";
import { useCurrency } from "@/context/CurrencyContext";
import { useState, useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  HardHat,
  Building2,
  Users,
  CheckCircle2,
  Star,
  ShieldCheck,
  ArrowRight,
  ClipboardList,
  CreditCard,
  BadgeCheck,
  PhoneCall,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NAVY = "#0f172a";
const ORANGE = "#fb3700";
const WHATSAPP = "17587230202";

const TRADES = [
  "General Labourer",
  "Painter",
  "Welder / Metalworker",
  "Mason / Concrete",
  "Tiler",
  "AC / HVAC Technician",
  "Carpenter / Joiner",
  "Window Cleaner",
  "Pest Control",
  "Roofer",
  "Cleaner / Janitorial",
  "Pool Maintenance",
  "Plumber",
  "Landscaper",
  "Electrician",
  "Other",
];

const PARISHES = [
  "Castries",
  "Gros Islet",
  "Soufriere",
  "Vieux Fort",
  "Micoud",
  "Dennery",
  "Choiseul",
  "Laborie",
  "Anse La Raye",
  "Canaries",
];

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const WORK_TYPES = [
  "Day work",
  "Short-term contract 1–4 weeks",
  "Long-term 1 month+",
  "Any",
];

const TIERS = [
  {
    name: "Standard",
    price: "EC$100",
    amount: 100,
    badge: null,
    highlight: false,
    features: [
      "Listed in the ProToCall Labour Registry",
      "Referred when your trade & availability match a request",
      "One-time fee, no renewal",
    ],
  },
  {
    name: "Premium",
    price: "EC$175",
    amount: 175,
    badge: "Most Popular",
    highlight: true,
    features: [
      "All Standard benefits",
      "Priority placement — shown first to hiring companies",
      "One-time fee, no renewal",
    ],
  },
  {
    name: "Verified Pro",
    price: "EC$250",
    amount: 250,
    badge: "Top Tier",
    highlight: false,
    features: [
      "All Premium benefits",
      "In-person trade skills assessment",
      "ProToCall Verified Pro badge on your profile",
      "Top priority for long-term & corporate contracts",
    ],
  },
];

const STEPS = [
  { icon: ClipboardList, title: "Complete the form", text: "Fill in your details, trade skills, and availability." },
  { icon: Star, title: "Choose your tier", text: "Standard, Premium, or Verified Pro." },
  { icon: CreditCard, title: "Pay one-time fee", text: "Secure payment via our payment portal." },
  { icon: BadgeCheck, title: "Get activated", text: "We review & activate your listing in 1–2 business days." },
  { icon: PhoneCall, title: "Get referred", text: "When a company needs your trade, we contact you first." },
];

const emptyForm = {
  full_name: "",
  date_of_birth: "",
  national_id: "",
  home_address: "",
  phone: "",
  email: "",
  parish: "",
  primary_trade: "",
  additional_trades: [],
  years_experience: "",
  experience_description: "",
  owns_tools: "",
  reliable_transport: "",
  has_drivers_license: "",
  driving_experience_years: "",
  can_lift_heavy_weight: "",
  can_operate_heavy_machinery: "",
  available_days: [],
  preferred_work_type: [],
  emergency_callouts: "",
  reference1_name: "",
  reference1_relationship: "",
  reference1_phone: "",
  reference2_name: "",
  reference2_relationship: "",
  reference2_phone: "",
  tier: "",
  agree_terms: false,
};

export default function LabourRegistryPage() {
  const { formatMoney } = useCurrency();
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [idCard, setIdCard] = useState(null);
  const [resume, setResume] = useState(null);
  const [promoCode, setPromoCode] = useState("");
  const [promo, setPromo] = useState(null);
  const [promoError, setPromoError] = useState("");
  const [searchParams] = useSearchParams();
  const formRef = useRef(null);

  useEffect(() => {
    if (searchParams.get("cancelled") === "1") {
      const saved = sessionStorage.getItem("registry_form");
      if (saved) {
        try {
          setForm(JSON.parse(saved));
        } catch (e) {
          // ignore
        }
      }
      toast.error("Payment was not completed. Please try again or contact ProToCall.");
      setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth" }), 300);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (field, value) => setForm((p) => ({ ...p, [field]: value }));

  const toggleArray = (field, value) => {
    setForm((p) => {
      const arr = p[field].includes(value)
        ? p[field].filter((v) => v !== value)
        : [...p[field], value];
      return { ...p, [field]: arr };
    });
  };

  const selectTier = (tierName) => {
    set("tier", tierName);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
  };

  const validate = () => {
    const required = [
      ["full_name", "Full Legal Name"],
      ["date_of_birth", "Date of Birth"],
      ["national_id", "National ID / Passport"],
      ["home_address", "Home Address"],
      ["phone", "Primary Phone / WhatsApp"],
      ["parish", "Parish / Area"],
      ["primary_trade", "Primary Trade"],
      ["years_experience", "Years of Experience"],
      ["owns_tools", "Own tools"],
      ["reliable_transport", "Reliable transport"],
      ["has_drivers_license", "Driver's license"],
      ["can_lift_heavy_weight", "Ability to lift heavy weight"],
      ["can_operate_heavy_machinery", "Forklift / heavy machinery operation"],
      ["emergency_callouts", "Emergency call-outs"],
      ["tier", "Listing Tier"],
    ];
    for (const [key, label] of required) {
      if (!form[key] && form[key] !== 0) {
        toast.error(`Please complete: ${label}`);
        return false;
      }
    }
    if (form.has_drivers_license === "Yes" && !form.driving_experience_years) {
      toast.error("Please enter your years of driving experience.");
      return false;
    }
    if (!form.agree_terms) {
      toast.error("Please agree to the Terms of Use to continue.");
      return false;
    }
    if (!idCard) {
      toast.error("A copy of your National ID card is required.");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const payload = {
        ...form,
        years_experience: parseInt(form.years_experience, 10) || 0,
        driving_experience_years:
          form.has_drivers_license === "Yes" ? parseInt(form.driving_experience_years, 10) || 0 : null,
        origin_url: window.location.origin,
      };
      delete payload.agree_terms;
      const res = await axios.post(`${API}/labour-registry`, payload);
      const appId = res.data.id;
      const fd = new FormData();
      fd.append("id_card", idCard);
      if (resume) fd.append("resume", resume);
      await axios.post(`${API}/labour-registry/${appId}/documents`, fd);
      sessionStorage.setItem("registry_form", JSON.stringify(form));
      const checkout = await axios.post(`${API}/labour-registry/${appId}/checkout`, {
        origin_url: window.location.origin,
        promo_code: promo ? promoCode.trim().toUpperCase() : null,
      });
      if (checkout.data.free) {
        window.location.href = `/labour-registry/success?free=1`;
        return;
      }
      window.location.href = checkout.data.url;
    } catch (error) {
      console.error("Registration error:", error);
      toast.error("Something went wrong. Please try again or contact ProToCall.");
      setLoading(false);
    }
  };

  const applyPromo = async () => {
    setPromoError("");
    if (!form.tier) {
      setPromoError("Please select a tier first.");
      return;
    }
    if (!promoCode.trim()) return;
    const selectedTier = TIERS.find((t) => t.name === form.tier);
    try {
      const { data } = await axios.post(`${API}/promo-codes/validate`, {
        code: promoCode.trim().toUpperCase(),
        service: "labour_registry",
        amount: selectedTier.amount,
      });
      if (data.valid) {
        setPromo(data);
        toast.success("Promo code applied!");
      } else {
        setPromo(null);
        setPromoError(data.error || "Invalid code");
      }
    } catch (e) {
      setPromo(null);
      setPromoError("Could not validate that code.");
    }
  };

  const radio = (field, value, label) => (
    <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-700">
      <input
        type="radio"
        name={field}
        checked={form[field] === value}
        onChange={() => set(field, value)}
        className="accent-orange-500 w-4 h-4"
        data-testid={`radio-${field}-${value.replace(/\s|\//g, "-").toLowerCase()}`}
      />
      {label}
    </label>
  );

  return (
    <div className="bg-white" data-testid="labour-registry-page">
      <Seo
        title="Labour Registry | ProToCall — Saint Lucia Skilled Trades"
        description="Join Saint Lucia's skilled trades database. Register once and get referred to companies looking for tradespeople. No business registration required — get found, get hired."
        path="/labour-registry"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Service",
          name: "ProToCall Labour Registry",
          serviceType: "Skilled trades registry and worker referral",
          provider: { "@type": "HomeAndConstructionBusiness", name: "ProToCall Ltd." },
          areaServed: { "@type": "AdministrativeArea", name: "Saint Lucia" },
        }}
      />

      {/* Hero */}
      <section style={{ backgroundColor: NAVY }} className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider mb-6"
              style={{ backgroundColor: "rgba(212,82,26,0.2)", color: "#ffc2b0" }}
            >
              <HardHat className="w-4 h-4" /> ProToCall Labour Registry
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-5">
              ProToCall Labour Registry
            </h1>
            <p className="text-lg md:text-xl text-slate-200 max-w-2xl mx-auto">
              Saint Lucia's skilled trades database.{" "}
              <span style={{ color: "#ffc2b0" }} className="font-semibold">
                Get found. Get hired.
              </span>
            </p>
          </motion.div>
        </div>
      </section>

      {/* Two-column intro */}
      <section className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-6 lg:gap-8">
          <div className="rounded-3xl p-8 md:p-10 text-white" style={{ backgroundColor: NAVY }} data-testid="intro-workers">
            <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center mb-5">
              <Users className="w-6 h-6" style={{ color: "#ffc2b0" }} />
            </div>
            <h2 className="text-2xl font-bold mb-3" style={{ color: "#ffc2b0" }}>
              For Skilled Workers
            </h2>
            <p className="text-slate-200 leading-relaxed mb-7">
              Register once and get your name in front of companies actively looking for skilled
              tradespeople across Saint Lucia. No business registration required. When a company
              needs your trade, ProToCall calls you.
            </p>
            <Button
              onClick={() => document.getElementById("registration-form")?.scrollIntoView({ behavior: "smooth" })}
              className="rounded-full font-semibold text-white hover:opacity-90"
              style={{ backgroundColor: ORANGE }}
              data-testid="intro-register-btn"
            >
              Register Now <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>

          <div className="rounded-3xl p-8 md:p-10 text-white" style={{ backgroundColor: ORANGE }} data-testid="intro-companies">
            <div className="w-12 h-12 rounded-xl bg-white/25 flex items-center justify-center mb-5">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-2xl font-bold mb-3">For Hiring Companies</h2>
            <p className="leading-relaxed mb-7 text-white/90">
              Need a painter, mason, carpenter, labourer, or any other tradesperson? Submit a
              request and we match you with available, vetted workers from our registry.{" "}
              <strong>Flat EC$150 referral fee per placement.</strong>
            </p>
            <Button
              asChild
              className="rounded-full font-semibold text-white hover:opacity-90"
              style={{ backgroundColor: NAVY }}
              data-testid="intro-request-btn"
            >
              <a
                href={`https://wa.me/${WHATSAPP}?text=${encodeURIComponent(
                  "Hi ProToCall, I'm a hiring company and would like to request a worker from the Labour Registry."
                )}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Request a Worker
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Trades Covered */}
      <section className="pb-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-10" style={{ color: NAVY }}>
            Trades Covered
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {TRADES.filter((t) => t !== "Other").map((trade) => (
              <div
                key={trade}
                className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3"
              >
                <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: ORANGE }} />
                <span className="text-sm text-slate-800">{trade}</span>
              </div>
            ))}
            <div className="flex items-center gap-2 px-4 py-3 text-sm font-semibold" style={{ color: ORANGE }}>
              And more…
            </div>
          </div>
        </div>
      </section>

      {/* Tiers */}
      <section className="py-16 md:py-20 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12" style={{ color: NAVY }}>
            Choose Your Listing Tier
          </h2>
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8 items-stretch">
            {TIERS.map((tier) => (
              <Card
                key={tier.name}
                className={`relative flex flex-col bg-white border-2 ${
                  tier.highlight ? "shadow-2xl" : "border-slate-200"
                }`}
                style={tier.highlight ? { borderColor: ORANGE } : {}}
                data-testid={`tier-card-${tier.name.replace(/\s/g, "-").toLowerCase()}`}
              >
                {tier.badge && (
                  <div
                    className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold text-white whitespace-nowrap"
                    style={{ backgroundColor: tier.highlight ? ORANGE : NAVY }}
                  >
                    {tier.badge}
                  </div>
                )}
                <CardContent className="p-8 flex flex-col flex-grow">
                  <h3 className="text-xl font-bold mb-1" style={{ color: NAVY }}>
                    {tier.name}
                  </h3>
                  <p className="text-3xl font-extrabold mb-1" style={{ color: ORANGE }}>
                    {formatMoney(tier.amount, { decimals: 0 })}
                  </p>
                  <p className="text-xs text-slate-500 mb-6">one-time fee</p>
                  <ul className="space-y-3 mb-8 flex-grow">
                    {tier.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
                        <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: ORANGE }} />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <Button
                    onClick={() => selectTier(tier.name)}
                    className="w-full rounded-full font-semibold text-white hover:opacity-90"
                    style={{ backgroundColor: tier.highlight ? ORANGE : NAVY }}
                    data-testid={`select-tier-${tier.name.replace(/\s/g, "-").toLowerCase()}`}
                  >
                    Select {tier.name}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-14" style={{ color: NAVY }}>
            How It Works — For Workers
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {STEPS.map((s, i) => (
              <div key={i} className="relative bg-white rounded-2xl p-6 border border-slate-200 shadow-sm" data-testid={`registry-step-${i + 1}`}>
                <div
                  className="absolute -top-3 -left-3 w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold shadow"
                  style={{ backgroundColor: ORANGE }}
                >
                  {i + 1}
                </div>
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: "rgba(27,58,107,0.08)" }}>
                  <s.icon className="w-5 h-5" style={{ color: NAVY }} />
                </div>
                <h3 className="font-semibold mb-2" style={{ color: NAVY }}>{s.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Registration Form */}
      <section id="registration-form" ref={formRef} className="py-16 md:py-20 bg-slate-50 scroll-mt-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-3" style={{ color: NAVY }}>
              Worker Registration Form
            </h2>
            <p className="text-slate-600">
              One-time registration. No renewal fees. Get in front of companies that need your skills.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-10" data-testid="registry-form">
            {/* Personal Information */}
            <div className="bg-white rounded-2xl p-6 md:p-8 border border-slate-200">
              <h3 className="font-bold text-lg mb-5" style={{ color: NAVY }}>Personal Information</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <Label className="mb-1.5 block">Full Legal Name *</Label>
                  <Input value={form.full_name} onChange={(e) => set("full_name", e.target.value)} data-testid="input-full-name" />
                </div>
                <div>
                  <Label className="mb-1.5 block">Date of Birth *</Label>
                  <Input type="date" value={form.date_of_birth} onChange={(e) => set("date_of_birth", e.target.value)} data-testid="input-dob" />
                </div>
                <div>
                  <Label className="mb-1.5 block">National ID / Passport No. *</Label>
                  <Input value={form.national_id} onChange={(e) => set("national_id", e.target.value)} data-testid="input-national-id" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="mb-1.5 block">Home Address *</Label>
                  <Textarea value={form.home_address} onChange={(e) => set("home_address", e.target.value)} data-testid="input-address" />
                </div>
                <div>
                  <Label className="mb-1.5 block">Primary Phone / WhatsApp *</Label>
                  <Input type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="input-phone" />
                </div>
                <div>
                  <Label className="mb-1.5 block">Email Address</Label>
                  <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="input-email" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="mb-1.5 block">Parish / Area of Residence *</Label>
                  <Select value={form.parish} onValueChange={(v) => set("parish", v)}>
                    <SelectTrigger data-testid="select-parish">
                      <span className={form.parish ? "" : "text-muted-foreground"}>{form.parish || "Select parish"}</span>
                    </SelectTrigger>
                    <SelectContent>
                      {PARISHES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Trade & Skills */}
            <div className="bg-white rounded-2xl p-6 md:p-8 border border-slate-200">
              <h3 className="font-bold text-lg mb-5" style={{ color: NAVY }}>Trade & Skills</h3>
              <div className="space-y-4">
                <div>
                  <Label className="mb-1.5 block">Primary Trade *</Label>
                  <Select value={form.primary_trade} onValueChange={(v) => set("primary_trade", v)}>
                    <SelectTrigger data-testid="select-primary-trade">
                      <span className={form.primary_trade ? "" : "text-muted-foreground"}>{form.primary_trade || "Select your primary trade"}</span>
                    </SelectTrigger>
                    <SelectContent>
                      {TRADES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="mb-2 block">Additional Trades</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {TRADES.map((t) => (
                      <label key={t} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                        <Checkbox
                          checked={form.additional_trades.includes(t)}
                          onCheckedChange={() => toggleArray("additional_trades", t)}
                          data-testid={`additional-trade-${t.replace(/\s|\//g, "-").toLowerCase()}`}
                        />
                        {t}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="mb-1.5 block">Years of Experience *</Label>
                    <Input type="number" min="0" value={form.years_experience} onChange={(e) => set("years_experience", e.target.value)} data-testid="input-experience" />
                  </div>
                </div>
                <div>
                  <Label className="mb-1.5 block">Brief description of experience</Label>
                  <Textarea maxLength={300} value={form.experience_description} onChange={(e) => set("experience_description", e.target.value)} placeholder="Max 300 characters" data-testid="input-experience-desc" />
                </div>
                <div>
                  <Label className="mb-2 block">Do you own your own tools?</Label>
                  <div className="flex flex-wrap gap-4">
                    {radio("owns_tools", "Yes — all tools", "Yes — all tools")}
                    {radio("owns_tools", "Yes — basic tools", "Yes — basic tools")}
                    {radio("owns_tools", "No", "No")}
                  </div>
                </div>
                <div>
                  <Label className="mb-2 block">Do you have reliable transport?</Label>
                  <div className="flex flex-wrap gap-4">
                    {radio("reliable_transport", "Yes", "Yes")}
                    {radio("reliable_transport", "No", "No")}
                    {radio("reliable_transport", "Sometimes", "Sometimes")}
                  </div>
                </div>
                <div>
                  <Label className="mb-2 block">Do you have a valid driver's license? *</Label>
                  <div className="flex flex-wrap gap-4">
                    {radio("has_drivers_license", "Yes", "Yes")}
                    {radio("has_drivers_license", "No", "No")}
                  </div>
                </div>
                {form.has_drivers_license === "Yes" && (
                  <div className="sm:max-w-xs">
                    <Label className="mb-1.5 block">Years of driving experience *</Label>
                    <Input
                      type="number"
                      min="0"
                      value={form.driving_experience_years}
                      onChange={(e) => set("driving_experience_years", e.target.value)}
                      data-testid="input-driving-experience"
                    />
                  </div>
                )}
                <div>
                  <Label className="mb-2 block">Can you lift heavy weight (e.g. 25kg+)? *</Label>
                  <div className="flex flex-wrap gap-4">
                    {radio("can_lift_heavy_weight", "Yes", "Yes")}
                    {radio("can_lift_heavy_weight", "No", "No")}
                  </div>
                </div>
                <div>
                  <Label className="mb-2 block">Can you operate a forklift or other heavy machinery? *</Label>
                  <div className="flex flex-wrap gap-4">
                    {radio("can_operate_heavy_machinery", "Yes", "Yes")}
                    {radio("can_operate_heavy_machinery", "No", "No")}
                  </div>
                </div>
              </div>
            </div>

            {/* Availability */}
            <div className="bg-white rounded-2xl p-6 md:p-8 border border-slate-200">
              <h3 className="font-bold text-lg mb-5" style={{ color: NAVY }}>Availability</h3>
              <div className="space-y-4">
                <div>
                  <Label className="mb-2 block">Available days</Label>
                  <div className="flex flex-wrap gap-3">
                    {DAYS.map((d) => (
                      <label key={d} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                        <Checkbox checked={form.available_days.includes(d)} onCheckedChange={() => toggleArray("available_days", d)} data-testid={`day-${d.toLowerCase()}`} />
                        {d}
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="mb-2 block">Preferred work type</Label>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {WORK_TYPES.map((w) => (
                      <label key={w} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                        <Checkbox checked={form.preferred_work_type.includes(w)} onCheckedChange={() => toggleArray("preferred_work_type", w)} data-testid={`worktype-${w.replace(/\s|–|\//g, "-").toLowerCase()}`} />
                        {w}
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="mb-2 block">Available for emergency call-outs?</Label>
                  <div className="flex gap-4">
                    {radio("emergency_callouts", "Yes", "Yes")}
                    {radio("emergency_callouts", "No", "No")}
                  </div>
                </div>
              </div>
            </div>

            {/* References */}
            <div className="bg-white rounded-2xl p-6 md:p-8 border border-slate-200">
              <h3 className="font-bold text-lg mb-5" style={{ color: NAVY }}>References</h3>
              <div className="space-y-4">
                <div className="grid sm:grid-cols-3 gap-4">
                  <Input placeholder="Reference 1 Name" value={form.reference1_name} onChange={(e) => set("reference1_name", e.target.value)} data-testid="ref1-name" />
                  <Input placeholder="Relationship" value={form.reference1_relationship} onChange={(e) => set("reference1_relationship", e.target.value)} data-testid="ref1-rel" />
                  <Input placeholder="Phone" type="tel" value={form.reference1_phone} onChange={(e) => set("reference1_phone", e.target.value)} data-testid="ref1-phone" />
                </div>
                <div className="grid sm:grid-cols-3 gap-4">
                  <Input placeholder="Reference 2 Name" value={form.reference2_name} onChange={(e) => set("reference2_name", e.target.value)} data-testid="ref2-name" />
                  <Input placeholder="Relationship" value={form.reference2_relationship} onChange={(e) => set("reference2_relationship", e.target.value)} data-testid="ref2-rel" />
                  <Input placeholder="Phone" type="tel" value={form.reference2_phone} onChange={(e) => set("reference2_phone", e.target.value)} data-testid="ref2-phone" />
                </div>
              </div>
            </div>

            {/* Documents */}
            <div className="bg-white rounded-2xl p-6 md:p-8 border border-slate-200">
              <h3 className="font-bold text-lg mb-1" style={{ color: NAVY }}>Documents</h3>
              <p className="text-sm text-slate-500 mb-5">Accepted formats: JPG, PNG, PDF or Word (max 10MB each).</p>
              <div className="space-y-5">
                <div>
                  <Label className="mb-1.5 block">
                    Copy of National ID Card <span style={{ color: ORANGE }}>*</span>
                  </Label>
                  <Input
                    type="file"
                    accept="image/*,application/pdf"
                    onChange={(e) => setIdCard(e.target.files?.[0] || null)}
                    className="cursor-pointer file:mr-3 file:rounded-full file:border-0 file:bg-slate-900 file:text-white file:px-4 file:py-1.5"
                    data-testid="upload-id-card"
                  />
                  {idCard && <p className="text-xs text-green-600 mt-1.5">Attached: {idCard.name}</p>}
                  <p className="text-xs text-slate-500 mt-1">Required — used to verify your identity. Kept private.</p>
                </div>
                <div>
                  <Label className="mb-1.5 block">
                    Resume / CV <span className="text-slate-400 font-normal">(optional, recommended)</span>
                  </Label>
                  <Input
                    type="file"
                    accept="image/*,application/pdf,.doc,.docx"
                    onChange={(e) => setResume(e.target.files?.[0] || null)}
                    className="cursor-pointer file:mr-3 file:rounded-full file:border-0 file:bg-slate-900 file:text-white file:px-4 file:py-1.5"
                    data-testid="upload-resume"
                  />
                  {resume && <p className="text-xs text-green-600 mt-1.5">Attached: {resume.name}</p>}
                  <p className="text-xs text-slate-500 mt-1">A CV helps companies choose you faster.</p>
                </div>
              </div>
            </div>

            {/* Tier Selection */}
            <div className="bg-white rounded-2xl p-6 md:p-8 border border-slate-200">
              <h3 className="font-bold text-lg mb-5" style={{ color: NAVY }}>Listing Tier Selection *</h3>
              <div className="space-y-3">
                {TIERS.map((tier) => (
                  <label
                    key={tier.name}
                    className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-colors ${
                      form.tier === tier.name ? "bg-orange-50" : "border-slate-200"
                    }`}
                    style={form.tier === tier.name ? { borderColor: ORANGE } : {}}
                    data-testid={`tier-radio-${tier.name.replace(/\s/g, "-").toLowerCase()}`}
                  >
                    <input type="radio" name="tier" checked={form.tier === tier.name} onChange={() => { set("tier", tier.name); setPromo(null); setPromoError(""); }} className="accent-orange-500 w-4 h-4 mt-1" />
                    <div>
                      <span className="font-semibold" style={{ color: NAVY }}>{tier.name} ({formatMoney(tier.amount, { decimals: 0 })})</span>
                      <p className="text-xs text-slate-500 mt-0.5">{tier.features[0]}{tier.features[1] ? ` · ${tier.features[1]}` : ""}</p>
                    </div>
                  </label>
                ))}
              </div>

              {/* Promo code */}
              <div className="mt-6 pt-5 border-t border-slate-200">
                <label className="text-sm font-semibold" style={{ color: NAVY }}>Have a promo code?</label>
                <div className="flex gap-2 mt-2">
                  <input
                    type="text"
                    value={promoCode}
                    onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); setPromo(null); setPromoError(""); }}
                    placeholder="Enter code"
                    className="flex-1 h-11 px-4 rounded-lg border border-slate-300 uppercase text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                    data-testid="registry-promo-input"
                  />
                  <Button
                    type="button"
                    onClick={applyPromo}
                    className="h-11 px-5 rounded-lg text-white font-semibold"
                    style={{ backgroundColor: NAVY }}
                    data-testid="registry-promo-apply"
                  >
                    Apply
                  </Button>
                </div>
                {promoError && <p className="text-xs text-red-600 mt-2" data-testid="registry-promo-error">{promoError}</p>}
                {promo && form.tier && (() => {
                  const t = TIERS.find((x) => x.name === form.tier);
                  return (
                    <div className="mt-3 rounded-lg bg-green-50 border border-green-200 p-3 text-sm" data-testid="registry-promo-summary">
                      <div className="flex justify-between text-slate-600"><span>Original</span><span>{formatMoney(t.amount)}</span></div>
                      <div className="flex justify-between text-green-700"><span>Discount ({promo.code})</span><span>−{formatMoney(promo.discount_amount)}</span></div>
                      <div className="flex justify-between font-bold mt-1 pt-1 border-t border-green-200" style={{ color: NAVY }}><span>You pay</span><span>{formatMoney(promo.final_amount)}</span></div>
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Terms */}
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox checked={form.agree_terms} onCheckedChange={(v) => set("agree_terms", !!v)} className="mt-0.5" data-testid="agree-terms" />
              <span className="text-sm text-slate-700">
                I confirm all information is accurate and I agree to the{" "}
                <Link to="/labour-registry/terms" target="_blank" className="font-semibold underline" style={{ color: ORANGE }}>
                  ProToCall Labour Registry Terms of Use
                </Link>
                .
              </span>
            </label>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-14 rounded-full text-white font-bold text-base hover:opacity-90"
              style={{ backgroundColor: ORANGE }}
              data-testid="submit-registration-btn"
            >
              {loading ? "Processing…" : "Complete Registration & Pay"}
              {!loading && <ArrowRight className="ml-2 w-5 h-5" />}
            </Button>
            <p className="text-center text-xs text-slate-500">
              Prices shown in your selected currency. Payment is securely processed in Eastern Caribbean Dollars (EC$/XCD) via ProToCall's payment portal.
            </p>
          </form>
        </div>
      </section>

      {/* Bottom CTA strip */}
      <section style={{ backgroundColor: NAVY }} className="py-14">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <div className="flex items-center justify-center gap-2 mb-4">
            <ShieldCheck className="w-5 h-5" style={{ color: "#ffc2b0" }} />
            <p className="text-lg font-semibold">Already registered and want to upgrade your tier?</p>
          </div>
          <p className="text-slate-300 mb-6">Contact ProToCall directly and we'll help you upgrade.</p>
          <Button
            asChild
            className="rounded-full font-semibold text-white hover:opacity-90"
            style={{ backgroundColor: ORANGE }}
            data-testid="upgrade-whatsapp-btn"
          >
            <a
              href={`https://wa.me/${WHATSAPP}?text=${encodeURIComponent(
                "Hi ProToCall, I would like to upgrade my Labour Registry listing."
              )}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              WhatsApp Us
            </a>
          </Button>
        </div>
      </section>
    </div>
  );
}
